import { useCallback, useEffect, useMemo, useState } from "react";
import { useUIMessages } from "@convex-dev/agent/react";
import { useApiMutation } from "@/packages/features-shared";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import type { ChatMessage, ToolPart } from "@/components/ui/ChatPanel";
import {
  useChatContextStructured,
  useChatContext,
} from "@/stores/chat-context-store";
import { useWorkspaceQuery, useWorkspaceId } from "@/hooks";
import { getCurrentLanguage } from "@/packages/utils";

const INITIAL_MESSAGE_COUNT = 50;

export interface GlobalThreadInfo {
  threadId: string;
  questionCount: number;
  firstQuestion: string;
  threadTitle: string | null;
  createdAt: number;
  lastMessageAt: number;
  documentIds: string[];
}

/**
 * Hook that manages global multi-thread chat:
 * - Lists all threads for the user (not scoped to a document)
 * - Supports creating new threads, switching, and deleting
 * - Accepts optional documentIds for context (empty = agent uses tools)
 * - Message history with streaming support
 */
export function useGlobalChat(documentIds: Id<"documents">[]) {
  const chatContext = useChatContextStructured();
  const [optimisticThreadId, setOptimisticThreadId] = useState<string | null>(
    null,
  );
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [isNewThread, setIsNewThread] = useState(false);
  const [awaitingResponse, setAwaitingResponse] = useState(false);

  const workspaceId = useWorkspaceId();

  // 1. List all threads for this user (not document-scoped)
  const { data: threads = [], isLoading: isThreadsLoading } = useWorkspaceQuery(
    api.documents.queries.stats.listThreadsForUser,
    {},
  );

  // Derive active threadId
  const threadId = isNewThread
    ? optimisticThreadId
    : (selectedThreadId ?? optimisticThreadId ?? threads[0]?.threadId ?? null);

  // 2. Fetch messages + streaming for the active thread
  const {
    results: rawMessages,
    status: paginationStatus,
    loadMore,
  } = useUIMessages(
    api.documents.queries.stats.listThreadMessages,
    threadId ? { threadId, workspaceId } : "skip",
    {
      initialNumItems: INITIAL_MESSAGE_COUNT,
      stream: true,
    },
  );

  // 3. Send messages via askQuestion mutation (documentIds can be empty)
  const askMutation = useApiMutation(api.documents.mutations.ai.askQuestion, {
    name: "global-chat-ask",
    silentSuccess: true,
    onSuccess: (rawData) => {
      const data = rawData;
      if (data.threadId) {
        setOptimisticThreadId(data.threadId);
        if (isNewThread) {
          setSelectedThreadId(data.threadId);
          setIsNewThread(false);
        }
      }
    },
  });

  // 4. Delete thread mutation
  const deleteMutation = useApiMutation(
    api.documents.mutations.ai.deleteThread,
    {
      name: "global-chat-delete-thread",
      successMessage: "Conversation deleted",
    },
  );

  // 5. Rename thread mutation
  const renameMutation = useApiMutation(
    api.documents.mutations.ai.renameThread,
    {
      name: "global-chat-rename-thread",
      successMessage: "Conversation renamed",
    },
  );

  // Map agent UIMessages to ChatPanel's ChatMessage format
  const messages: ChatMessage[] = useMemo(() => {
    if (!threadId || !rawMessages?.length) return [];

    return rawMessages
      .filter(
        (msg: { role: string }) =>
          msg.role === "user" || msg.role === "assistant",
      )
      .map(
        (msg: {
          key?: string;
          order: number;
          stepOrder: number;
          role: string;
          text?: string;
          _creationTime?: number;
          parts?: Array<{ type: string; state?: string; output?: unknown }>;
        }) => {
          // Extract tool parts from message parts array (all states for loading indicators)
          const toolParts: ToolPart[] = (msg.parts ?? [])
            .filter(
              (p): p is { type: string; state: string; output?: unknown } =>
                typeof p.type === "string" &&
                p.type.startsWith("tool-") &&
                typeof p.state === "string",
            )
            .map((p) => ({
              toolName: p.type.replace("tool-", ""),
              state: p.state,
              output: p.output,
            }));

          return {
            id: msg.key ?? `${msg.order}-${msg.stepOrder}`,
            role: msg.role as "user" | "assistant",
            content: msg.text ?? "",
            timestamp: msg._creationTime
              ? new Date(msg._creationTime).toISOString()
              : undefined,
            ...(toolParts.length > 0 ? { toolParts } : {}),
          };
        },
      );
  }, [rawMessages, threadId]);

  // Detect streaming
  const isStreaming = useMemo(() => {
    if (!threadId || !rawMessages?.length) return false;
    return rawMessages.some(
      (msg: { status?: string }) => msg.status === "streaming",
    );
  }, [rawMessages, threadId]);

  // Clear awaitingResponse once messages arrive
  useEffect(() => {
    if (awaitingResponse && messages.length > 0) {
      setAwaitingResponse(false);
    }
  }, [awaitingResponse, messages.length]);

  const isFirstMessage = !threadId;

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim()) return;

      // Show loading immediately for first message
      if (!threadId) setAwaitingResponse(true);

      try {
        await askMutation.mutateAsync({
          question: text.trim(),
          documentIds: documentIds.length > 0 ? documentIds : undefined,
          locale: getCurrentLanguage(),
          ...(threadId ? { threadId } : {}),
          context: chatContext
            ? {
                page: chatContext.page,
                pageLabel: chatContext.pageLabel,
                entities: chatContext.entities,
              }
            : undefined,
          workspaceId,
        });
      } catch {
        setAwaitingResponse(false);
      }
    },
    [documentIds, threadId, askMutation, chatContext, workspaceId],
  );

  const startNewThread = useCallback(() => {
    setSelectedThreadId(null);
    setOptimisticThreadId(null);
    setIsNewThread(true);
    setAwaitingResponse(false);
    useChatContext.getState().clearEntities();
  }, []);

  const selectThread = useCallback((id: string) => {
    setSelectedThreadId(id);
    setOptimisticThreadId(null);
    setIsNewThread(false);
    setAwaitingResponse(false);
  }, []);

  const deleteThread = useCallback(
    async (id: string) => {
      await deleteMutation.mutateAsync({ threadId: id, workspaceId });

      if (threadId === id) {
        setSelectedThreadId(null);
        setOptimisticThreadId(null);
        setIsNewThread(false);
      }
    },
    [deleteMutation, threadId, workspaceId],
  );

  const renameThread = useCallback(
    async (id: string, title: string) => {
      await renameMutation.mutateAsync({ threadId: id, title, workspaceId });
    },
    [renameMutation, workspaceId],
  );

  return {
    messages,
    sendMessage,
    isStreaming,
    isSending: askMutation.isPending,
    isLoading:
      isThreadsLoading ||
      awaitingResponse ||
      (!!threadId && paginationStatus === "LoadingFirstPage"),
    isFirstMessage,
    threadId,
    loadMore,
    hasMore: paginationStatus === "CanLoadMore",
    // Multi-thread
    threads,
    startNewThread,
    selectThread,
    deleteThread,
    renameThread,
    isDeleting: deleteMutation.isPending,
    isRenaming: renameMutation.isPending,
  };
}
